import React from 'react';
import { Store, Clock, ChevronRight, Home } from 'lucide-react';

// Modal de "Loja Fechada / Indisponível" do cardápio público.
// Extraído VERBATIM de PublicMenu.js (Fase 2 refactor). Componente puro (sem hooks),
// recebe estado/handlers via props. Renderizado como <ClosedStoreModal .../>.
const ClosedStoreModal = ({
  showClosedStoreModal,
  getNextOpeningInfo,
  restaurant,
  setShowClosedStoreModal,
  setShowStoreProfile,
  setCurrentScreen,
}) => {
  if (!showClosedStoreModal) return null;

  const nextOpeningInfo = getNextOpeningInfo();
  const isExpired = restaurant?.status?.is_expired === true;
  const isSuspended = restaurant?.status?.is_suspended === true;

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm"
        onClick={() => setShowClosedStoreModal(false)}
      />
      
      {/* Modal Content */}
      <div className="relative bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md shadow-2xl animate-slide-up">
        {/* Header com gradiente */}
        <div className={`p-6 rounded-t-3xl sm:rounded-t-2xl ${
          isExpired || isSuspended 
            ? 'bg-gradient-to-br from-red-500 to-orange-500' 
            : 'bg-gradient-to-br from-orange-500 to-red-500'
        }`}>
          <div className="text-center">
            {/* Icon */}
            <div className="mx-auto w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center mb-4 backdrop-blur-sm">
              <Store className="w-10 h-10 text-white" />
            </div>
            
            {/* Title */}
            <h3 className="text-2xl font-bold text-white mb-2">
              {isExpired || isSuspended ? '🔒 Loja Temporariamente Indisponível' : 'Ops! Estamos Fechados'}
            </h3>
            <p className="text-white text-opacity-90 text-sm">
              {isExpired || isSuspended 
                ? 'Esta loja está temporariamente desativada'
                : 'No momento não estamos aceitando pedidos'}
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Current Status */}
          <div className={`border-2 rounded-xl p-4 ${
            isExpired || isSuspended 
              ? 'bg-red-50 border-red-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-0.5">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
              </div>
              <div className="flex-1">
                <p className="text-red-800 font-semibold text-sm mb-1">
                  Status Atual
                </p>
                <p className="text-red-700 text-sm">
                  {isExpired || isSuspended 
                    ? 'Estabelecimento temporariamente fechado. Você pode visualizar o cardápio, mas não é possível fazer pedidos no momento.'
                    : restaurant?.status?.message || 'Estabelecimento fechado'}
                </p>
              </div>
            </div>
          </div>

          {/* Next Opening - Only show if not expired/suspended */}
          {!isExpired && !isSuspended && nextOpeningInfo && (
            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <Clock className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-green-800 font-semibold text-sm mb-2">
                    Próxima Abertura
                  </p>
                  <div className="space-y-1">
                    <p className="text-green-700 text-base font-bold">
                      {nextOpeningInfo.day === 'hoje' ? 'Hoje' : nextOpeningInfo.day === 'amanhã' ? 'Amanhã' : nextOpeningInfo.fullDay}
                    </p>
                    <p className="text-green-600 text-sm">
                      às {nextOpeningInfo.time}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Opening Hours Schedule - Only show if not expired/suspended */}
          {!isExpired && !isSuspended && restaurant?.status?.schedule && (
            <div className="border-t border-gray-200 pt-4">
              <button
                onClick={() => setShowStoreProfile(true)}
                className="w-full flex items-center justify-between text-left py-2 px-3 rounded-lg hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary-light rounded-full flex items-center justify-center group-hover:bg-primary-light transition-colors">
                    <Clock className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      Ver Horário de Funcionamento
                    </p>
                    <p className="text-xs text-gray-500">
                      Confira todos os nossos horários
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
              </button>
            </div>
          )}

          {/* Action Button */}
          <button
            onClick={() => {
              setShowClosedStoreModal(false);
              setCurrentScreen('home');
            }}
            className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-4 px-6 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl active:scale-95 flex items-center justify-center space-x-2"
            style={{ minHeight: '52px' }}
          >
            <Home className="w-5 h-5" />
            <span>Voltar ao Cardápio</span>
          </button>

          {/* Info Footer */}
          <p className="text-center text-xs text-gray-500">
            💡 Você pode explorar nosso cardápio enquanto aguarda
          </p>
        </div>
      </div>
    </div>
  );
};

export default ClosedStoreModal;
